import {useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode} from 'react'
import {SkeletonText} from '@primer/react/experimental'
import {AlertIcon} from '@primer/octicons-react'
import {MessageCircleIcon} from '@github-ui/custom-icons/MessageCircleIcon'
import {useSearchParams} from '@github-ui/use-navigate'
import {clsx} from 'clsx'
import type {SidebarChat} from '../../types/sidebar-chat'
import {getSidebarChatHref, isSidebarChatCurrent} from '../../hooks/use-legacy-sidebar-chats'
import {generateTaskPathForRepo} from '../../utils/generate-task-path'
import {isSessionState, SessionState} from '../../types/session'
import {SessionsNavGroup} from './SessionsByRepoNav/SessionsNavGroup'
import stateStyles from './SidebarState.module.css'
import {getAgentsPath} from '../../utils/constants'
import {SessionsNavItem} from './SessionsByRepoNav/SessionsNavItem'
import type {SessionsNavItemProps} from './SessionsByRepoNav/types'
import {SessionsNavListShowAction} from './SessionsByRepoNav/SessionsNavListShowAction'

type ChatNavigationHandler = (href: string, event: MouseEvent<HTMLElement>) => void

const INITIAL_CHAT_COUNT = 5
const SKELETON_ROW_WIDTHS = ['88%', '70%', '80%']

export interface AgentsChatsState {
  chats: readonly SidebarChat[]
  isLoadingChats: boolean
  isErrorChats: boolean
  /** True while older chats remain on the server beyond the pages already loaded. */
  hasMoreChats?: boolean
  isLoadingMoreChats?: boolean
  /** True when fetching an older page failed; the pages already loaded remain usable. */
  hasLoadMoreError?: boolean
  loadMoreChats?: () => void
  apiURL?: string
  onTaskStateChange?: (taskId: string) => void
  partialErrorMessage?: string
}

export interface ChatsNavGroupProps {
  agentsChats?: AgentsChatsState
  /** The parent already reports the full failure alongside the sessions error. */
  suppressFullError?: boolean
  chatApiURL?: string
  currentTaskId?: string
  activeThreadId?: string
  getStatusLabel?: (chat: SidebarChat) => string
  onNavigate?: ChatNavigationHandler
  onLegacyNavigate?: ChatNavigationHandler
  onNewChat?: ChatNavigationHandler
  showNewChat?: boolean
}

function getChatSessionState(state: string) {
  if (state === 'running') return SessionState.InProgress
  if (state === 'stopped') return SessionState.Cancelled
  return isSessionState(state) ? state : undefined
}

export function getChatSessionNavItemProps({
  chat,
  href,
  isCurrent,
  chatApiURL,
  getStatusLabel,
  onNavigate,
}: {
  chat: SidebarChat
  href: string
  isCurrent: boolean
  chatApiURL?: string
  getStatusLabel?: ChatsNavGroupProps['getStatusLabel']
  onNavigate?: ChatNavigationHandler
}): SessionsNavItemProps {
  const currentEnvironment = chat.environment?.id ? {id: chat.environment.id, kind: chat.environment.kind} : undefined
  return {
    task: {
      id: chat.taskId,
      name: chat.title,
      taskType: 'chat',
      agentSlug: chat.agentSlug,
      latest_session_state: chat.legacy ? undefined : getChatSessionState(chat.latestSessionState),
      latest_session_id: chat.latestSessionId,
      latest_session_updated_at: chat.updatedAt,
      creator_id: chat.creatorId,
      nwo: chat.nwo,
      sharing_status: chat.sharingStatus,
      archived_at: chat.archivedAt,
      current_environment: currentEnvironment,
    },
    href,
    isSelected: isCurrent,
    unavailableReason: chat.resumable ? undefined : chat.resumeBlocker || 'This chat can’t be resumed.',
    statusLabel: chat.legacy ? undefined : getStatusLabel?.(chat),
    onClick: event => onNavigate?.(href, event),
    environmentStatusApiURL: chatApiURL,
    readOnly: Boolean(chat.legacy),
    enablePinning: !chat.legacy,
    ssoRequired: chat.legacy?.ssoRequired,
  }
}

export function ChatsNavGroup({
  agentsChats,
  suppressFullError = false,
  chatApiURL,
  currentTaskId,
  activeThreadId,
  getStatusLabel,
  onNavigate,
  onLegacyNavigate,
  onNewChat,
  showNewChat = true,
}: ChatsNavGroupProps) {
  const [searchParams] = useSearchParams()
  const newChatHref = getAgentsPath('?chat=new')
  const [showAllChats, setShowAllChats] = useState(false)
  const chats = agentsChats?.chats
  const hasHistoryRows = Boolean(agentsChats && !agentsChats.isErrorChats && agentsChats.chats.length > 0)
  const hasUnloadedChats = Boolean(agentsChats?.hasMoreChats)
  const showChatsNav = Boolean(
    agentsChats &&
    (agentsChats.isLoadingChats ||
      agentsChats.isErrorChats ||
      hasHistoryRows ||
      hasUnloadedChats ||
      agentsChats.partialErrorMessage),
  )
  const activeChatIndex = useMemo(
    () => chats?.findIndex(chat => isSidebarChatCurrent(chat, {activeChatTaskId: currentTaskId, activeThreadId})) ?? -1,
    [chats, currentTaskId, activeThreadId],
  )
  const activeChatIsHidden = activeChatIndex >= INITIAL_CHAT_COUNT
  const isExpanded = showAllChats || activeChatIsHidden
  const visibleCount = isExpanded ? (chats?.length ?? 0) : INITIAL_CHAT_COUNT
  const hasLoadedTail = (chats?.length ?? 0) > visibleCount
  const hasMoreChats = hasLoadedTail || hasUnloadedChats
  const showListControls = (hasHistoryRows || hasUnloadedChats) && !agentsChats?.isLoadingChats
  const showMoreVisible = showListControls && hasMoreChats
  const showLessVisible = showListControls && showAllChats && isExpanded && !activeChatIsHidden
  const loadMoreChats = agentsChats?.loadMoreChats
  const showLessRef = useRef<HTMLElement>(null)
  const groupRef = useRef<HTMLDivElement>(null)
  const restoreFocusRef = useRef(false)
  const onShowLessRef = useCallback((element: HTMLElement | null) => {
    showLessRef.current = element
  }, [])
  const handleShowMore = useCallback(() => {
    // Reveal the already-loaded tail first; only reach for another page once it runs out.
    if (hasUnloadedChats && !hasLoadedTail) loadMoreChats?.()
    restoreFocusRef.current = true
    setShowAllChats(true)
  }, [hasLoadedTail, hasUnloadedChats, loadMoreChats])
  // "Show more..." unmounts once the last page arrives; move focus rather than drop it.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Focus moves after the list updates.
    if (!restoreFocusRef.current || showMoreVisible) return
    restoreFocusRef.current = false
    if (showLessRef.current) {
      showLessRef.current.focus()
    } else {
      const fallback =
        groupRef.current?.parentElement?.querySelector<HTMLElement>('[aria-current="page"]') ??
        groupRef.current?.querySelector<HTMLButtonElement>('button[aria-expanded]')
      fallback?.focus()
    }
  }, [showMoreVisible])
  const handleShowLess = useCallback(() => setShowAllChats(false), [])
  const handleNewChatClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      onNewChat?.(newChatHref, event)
    },
    [newChatHref, onNewChat],
  )

  let content: ReactNode
  if (agentsChats?.isErrorChats) {
    content = suppressFullError ? null : (
      <li className={clsx(stateStyles.state, stateStyles.stateError, stateStyles.constrained)}>
        <AlertIcon aria-hidden="true" className={stateStyles.stateIcon} />
        <p className={stateStyles.stateTitle}>Couldn’t load chats</p>
        <p className={stateStyles.stateDescription}>Check your connection and try again.</p>
      </li>
    )
  } else if (agentsChats?.isLoadingChats && chats?.length === 0) {
    content = (
      <li className={clsx(stateStyles.skeleton, stateStyles.constrained)}>
        <div aria-hidden="true">
          {SKELETON_ROW_WIDTHS.map(width => (
            <div key={width} className={stateStyles.skeletonRow}>
              <SkeletonText maxWidth={width} />
            </div>
          ))}
        </div>
      </li>
    )
  } else {
    const visibleChats = chats?.slice(0, visibleCount)
    content = visibleChats?.map(chat => (
      <SessionsNavItem
        key={chat.legacy ? `legacy-${chat.taskId}` : chat.taskId}
        {...getChatSessionNavItemProps({
          chat,
          href: getSidebarChatHref(chat, generateTaskPathForRepo({taskId: chat.taskId}), searchParams),
          isCurrent: isSidebarChatCurrent(chat, {activeChatTaskId: currentTaskId, activeThreadId}),
          chatApiURL,
          getStatusLabel,
          onNavigate: chat.legacy ? onLegacyNavigate : onNavigate,
        })}
        chatTypeContextProvidedByGroup
      />
    ))
  }

  return (
    <SessionsNavGroup
      groupKey="chats"
      label="Chats"
      icon={MessageCircleIcon}
      variant="chats"
      rowRef={groupRef}
      collapsible={showChatsNav}
      newSessionHref={showNewChat ? newChatHref : undefined}
      onNewSessionClick={handleNewChatClick}
      newSessionButtonVisible={!hasHistoryRows}
    >
      {showChatsNav && agentsChats ? (
        <>
          {agentsChats.partialErrorMessage && !agentsChats.isErrorChats && (
            <li className={clsx(stateStyles.state, stateStyles.stateError, stateStyles.constrained)}>
              <AlertIcon aria-hidden="true" className={stateStyles.stateIcon} />
              <p className={stateStyles.stateTitle}>{agentsChats.partialErrorMessage}</p>
            </li>
          )}
          {content}
          {showMoreVisible && (
            <SessionsNavListShowAction
              onClick={handleShowMore}
              aria-expanded={isExpanded}
              data-testid="chats-show-more"
              hiddenLabel="chats"
            >
              {agentsChats.isLoadingMoreChats
                ? 'Loading...'
                : agentsChats.hasLoadMoreError
                  ? 'Retry loading older'
                  : 'Show more...'}
            </SessionsNavListShowAction>
          )}
          {showLessVisible && (
            <SessionsNavListShowAction
              onClick={handleShowLess}
              data-testid="chats-show-less"
              ref={onShowLessRef}
              hiddenLabel="chats"
            >
              Show less
            </SessionsNavListShowAction>
          )}
        </>
      ) : null}
    </SessionsNavGroup>
  )
}
